// The files our team gets: one Illustrator-ready SVG per cover and side, plus a preview PNG for each.
// Ported from the prototype (coverSVG / buildFiles). Same groups and naming:
//   Background_design_reference_low_res, Customer_design (Photo_n, Text_n), Print_line_guide_do_not_print.
// Changes: the picture is written once per image (xlink:href only), which halves the file size,
// the print size and product name come from the product settings, and a double-sided product gets
// one file pair per side that has a customer design (a side left empty gets none: it keeps the original design).
// A bulk order with different names gets ONE master per side (not one per name). In a master the text that
// carries the name has the id "Name_text" and holds the first name. The staff page makes one file per name
// from it by replacing that text (names-zip.js).

import { xmlEsc, slug } from "./util.js";

const MAX_PREVIEW_BYTES = 4.5 * 1024 * 1024;        // the upload service allows 5 MB per preview
const PREVIEW_WIDTHS = [1200, 900, 640];            // try the biggest first, go smaller if the file is too big
const n4 = (v) => +v.toFixed(4);

const readAsDataURL = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => rej(r.error || new Error("read")); r.readAsDataURL(blob); });
const toBlob = (canvas, type) => new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("encode"))), type));

// The photo of the side that is showing, as a JPEG (Illustrator reads JPEG and PNG inside SVG, not WebP).
// One copy per photo, so the front and the back each get their own.
const bgCache = new WeakMap();
function backgroundJpeg(ed) {
  const img = ed.photoImg;
  if (!bgCache.has(img)) {
    const { imageW, imageH } = ed.area;
    const c = document.createElement("canvas"); c.width = imageW; c.height = imageH;     // any size: not always square
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, imageW, imageH);                          // a photo with see-through parts is not turned black by the JPEG
    ctx.drawImage(img, 0, 0, imageW, imageH);
    bgCache.set(img, c.toDataURL("image/jpeg", 0.92));
  }
  return bgCache.get(img);
}

async function photoSVG(ed, o, idx, dataUrls) {
  const M = o.calcTransformMatrix();
  const orig = o.rgId && ed.photos().find((p) => p.rgId === o.rgId)?.original;
  let href, w, h, note;
  if (orig && o.rgOrigW) {
    if (!dataUrls.has(o.rgId)) dataUrls.set(o.rgId, await readAsDataURL(orig));
    href = dataUrls.get(o.rgId); w = o.rgOrigW; h = o.rgOrigH; note = "original resolution";
  } else { href = o.getSrc(); w = o.width; h = o.height; note = "preview resolution (original not on this device)"; }
  const k = o.width / w;                                   // the editor shows a smaller copy; place the original in its spot
  const m = [M[0] * k, M[1] * k, M[2] * k, M[3] * k, M[4], M[5]].map(n4).join(" ");
  return `    <image id="Photo_${idx}" data-name="${xmlEsc(o.rgLabel || "Photo " + idx)} (${w}x${h}px, ${note})" transform="matrix(${m})" x="${n4(-w / 2)}" y="${n4(-h / 2)}" width="${w}" height="${h}" preserveAspectRatio="none" xlink:href="${href}"/>`;
}

// nameId: "Name_text" (or "Name_text_2" ...) when this text carries the name in a master, else "".
// The name text is centred on its own middle (text-anchor), so a longer or shorter name still sits in the same place.
function textSVG(o, idx, nameId = "") {
  const clip = o.clipPath; o.clipPath = undefined;
  let svg = o.toSVG(); o.clipPath = clip;
  if (nameId) {
    svg = svg.replace("<text ", `<text id="${nameId}" ${o.textAlign === "center" ? 'text-anchor="middle" ' : ""}`);
    if (o.textAlign === "center") svg = svg.replace(/(<tspan\b[^>]*?\s)x="[^"]*"/, '$1x="0"');
  }
  const label = nameId ? `Name (${xmlEsc(o.fontFamily)} ${o.fontWeight})` : `${xmlEsc(o.text)} (${xmlEsc(o.fontFamily)} ${o.fontWeight})`;
  return `    <g id="Text_${idx}" data-name="${label}">\n${svg}\n    </g>`;
}

// One cover's SVG as text, for the side the editor is showing (see ed.withSide).
// The editor must be showing this cover's name (see ed.withCover).
// master: true makes the master of a bulk order with names (the title has "[name]" and the name text has an id).
export async function coverSVG(ed, coverName, dataUrls = new Map(), { master = false } = {}) {
  const { points, x, y, w, h, imageW, imageH } = ed.area;
  const { printWidthCm, title } = ed.product;
  const all = ed.sides(), sideLabel = all.length > 1 ? ` - ${all[ed.sideIndex()].label}` : "";
  const pts = points.map((p) => `${p.x},${p.y}`).join(" ");
  const hCm = (h / w) * printWidthCm;
  const parts = []; let pi = 0, ti = 0, ni = 0;
  for (const o of ed.userObjects()) {
    if (o.rgKind === "photo") parts.push(await photoSVG(ed, o, ++pi, dataUrls));
    else if (o.rgKind === "text") parts.push(textSVG(o, ++ti, master && o.rgIsName ? (++ni === 1 ? "Name_text" : `Name_text_${ni}`) : ""));
  }
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="${printWidthCm}cm" height="${hCm.toFixed(2)}cm" viewBox="${x} ${y} ${w} ${h}">
  <title>RightGifting - ${xmlEsc(title)} - ${master ? "[name]" : xmlEsc(coverName)}${xmlEsc(sideLabel)}</title>
  <desc>Made with the RightGifting personaliser. Customer photos are embedded at original resolution. Text is live: install the free Google fonts used to edit it. One design for every size: scale as usual.</desc>
  <defs><clipPath id="print-area"><polygon points="${pts}"/></clipPath></defs>
  <g id="Background_design_reference_low_res" clip-path="url(#print-area)">
    <image width="${imageW}" height="${imageH}" xlink:href="${backgroundJpeg(ed)}"/>
  </g>
  <g id="Customer_design" clip-path="url(#print-area)">
${parts.join("\n")}
  </g>
  <g id="Print_line_guide_do_not_print" fill="none" stroke="#12a866" stroke-width="2" stroke-dasharray="9 6">
    <polygon points="${pts}"/>
  </g>
</svg>
`;
}

// The print area as a PNG, clipped to the outline (outside the outline is see-through). Shows the design as printed on the cover.
// The editor must be showing the cover you want (see ed.withCover).
async function renderPreviewBlob(ed) {
  const { points, x, y, w } = ed.area;
  let blob = null;
  for (const maxW of PREVIEW_WIDTHS) {
    const src = ed.renderArea(Math.min(2, maxW / w));
    const c = document.createElement("canvas"); c.width = src.width; c.height = src.height;
    const ctx = c.getContext("2d"), k = src.width / w;
    ctx.beginPath(); points.forEach((p, i) => ctx[i ? "lineTo" : "moveTo"]((p.x - x) * k, (p.y - y) * k)); ctx.closePath(); ctx.clip();
    ctx.drawImage(src, 0, 0);
    blob = await toBlob(c, "image/png");
    if (blob.size <= MAX_PREVIEW_BYTES) break;
  }
  return blob;
}

// A PNG of the print area for cover number coverIndex (0 is the first), on side number sideIndex
// (default: the side that is showing). Max about 1200 px wide.
export function previewPNG(ed, coverIndex = 0, sideIndex = ed.sideIndex()) {
  return ed.withSide(sideIndex, () => ed.withCover(coverIndex, () => renderPreviewBlob(ed)));
}

// Everything to upload on Confirm. There is always one cover. For a bulk order with names it is the master,
// built with the first name. Each cover has one entry per side of the product:
//   { id, label, svgFilename, svgBlob, previewFilename, previewBlob }   the side has a customer design
//   { id, label, keepOriginal: true }                                   the side was left empty: no files
// Returns { covers: [{ index, name, sides: [...] }], photos: [{ rgId, filename, file }], namesBlob }
// namesBlob is the list of names as JSON ([{ name, size }]) for a bulk order with names, else null.
export async function buildFiles(ed, onProgress) {
  const { master, list } = ed.coverPlan();
  const productSlug = slug(ed.product.title).toLowerCase() || ed.product.id;
  const dataUrls = new Map();
  const sideList = ed.sides();
  const covers = [];
  for (let i = 0; i < list.length; i++) {
    if (onProgress) onProgress(i, list.length);
    const num = String(i + 1).padStart(2, "0"), safe = slug(list[i]) || "design";
    await ed.withCover(i, async () => {
      const sides = [];
      for (const s of sideList) {
        if (!s.hasDesign) { sides.push({ id: s.id, label: s.label, keepOriginal: true }); continue; }
        await ed.withSide(s.index, async () => {
          const svg = await coverSVG(ed, list[i], dataUrls, { master });
          const previewBlob = await renderPreviewBlob(ed);
          sides.push({
            id: s.id, label: s.label,
            svgFilename: master ? `${productSlug}_master_${s.id}.svg` : `${productSlug}_${num}_${safe}_${s.id}.svg`,
            svgBlob: new Blob([svg], { type: "image/svg+xml" }),
            previewFilename: master ? `preview_master_${s.id}.png` : `preview_${num}_${safe}_${s.id}.png`,
            previewBlob
          });
        });
      }
      covers.push({ index: i + 1, name: "", sides });
    });
  }
  if (onProgress) onProgress(list.length, list.length);
  const photos = ed.photos().filter((p) => p.original).map((p) => ({
    rgId: p.rgId,
    filename: (p.original.name || "photo").replace(/[\u0000-\u001f\\/]/g, "").slice(0, 120) || "photo",
    file: p.original
  }));
  const namesBlob = master ? new Blob([JSON.stringify(ed.bulk.names())], { type: "application/json" }) : null;
  return { covers, photos, namesBlob };
}
